import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import "~/App.css";
import "~/css/showSessionPopup.css";

const sessionTimeoutSeconds = 5; // セッションのタイムアウト時間（秒）
const countdownIntervalMs = 1000; // カウントダウンの更新間隔（ミリ秒）
const cleanupDelayMs = 0; // ポップアップ削除の遅延時間（ミリ秒）
const lastSecond = 1; // 最後の1秒を示す定数

const SessionPopup: React.FC<{
  onContinue: () => void;
  onDisconnect: () => void;
  onAutoClose: () => void;
}> = ({ onContinue, onAutoClose }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState(sessionTimeoutSeconds);
  const [isManuallyClosed, setIsManuallyClosed] = useState(false);

  useEffect(() => {
    const handleClose = () => {
      handleContinue();
    };
    if (isManuallyClosed) {
      return () => {
        // 手動で閉じた場合やフラグがtrueの場合は自動閉じる処理をスキップ
      };
    }
    window.addEventListener("close-popup", handleClose);

    const countdown = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev === lastSecond && !isManuallyClosed) {
          clearInterval(countdown);
          setIsVisible(false);
          onAutoClose(); // 自動的に閉じたときの処理を呼び出す
        }
        return prev - 1;
      });
    }, countdownIntervalMs);

    return () => {
      clearInterval(countdown);
      window.removeEventListener("close-popup", handleClose);
    };
  }, [isManuallyClosed, onAutoClose]);

  const handleContinue = () => {
    setIsManuallyClosed(true); // 手動で閉じたことを記録
    setIsVisible(false);
    onContinue();
  };

  if (!isVisible) return null;

  return (
    <div className="overlay">
      <div className="popup">
        <h2>{t("sessionWillEnd", { seconds: secondsLeft })}</h2>
        <br />
        <button className="connect" onClick={handleContinue}>
          {t("continueSession")}
        </button>
      </div>
    </div>
  );
};

// ポップアップを表示する関数
const showSessionPopup = (
  onContinue: () => void,
  onDisconnect: () => void,
  onAutoClose: () => void
) => {
  const popupRoot = document.createElement("div");
  document.body.appendChild(popupRoot);

  const handleContinue = () => {
    onContinue();
    cleanup();
  };

  const handleDisconnect = () => {
    onDisconnect();
    cleanup();
  };

  const cleanup = () => {
    setTimeout(() => {
      if (root) {
        root.unmount();
      }
      if (document.body.contains(popupRoot)) {
        document.body.removeChild(popupRoot);
      }
    }, cleanupDelayMs); // 非同期に実行
  };
  const root = createRoot(popupRoot);
  root.render(
    <SessionPopup
      onContinue={handleContinue}
      onDisconnect={handleDisconnect}
      onAutoClose={onAutoClose}
    />
  );
};
// カスタムイベントをトリガーしてポップアップを閉じる
const triggerClosePopup = () => {
  const event = new CustomEvent("close-popup");
  window.dispatchEvent(event);
};
export { showSessionPopup, triggerClosePopup };
